import api from "./client";
import type {
  CommitFailData,
  LookaheadBoard,
  LookaheadPpc,
  PpcAssigneeRow,
  PpcReasonItem,
  TaskConstraint,
  TaskConstraintFormData,
  TaskConstraintStatus,
} from "../types";

// ---- Lookahead panosu + PPC ----

/** GET /lookahead?project_id=&weeks= — haftalık kovalar (boş haftalar da döner). */
export async function fetchLookahead(params: {
  project_id?: number;
  weeks?: number;
}): Promise<LookaheadBoard> {
  const response = await api.get("/api/v1/lookahead", { params });
  return response.data.data;
}

/**
 * Backend PPC neden dağılımını tek biçime indirger. Sözleşme henüz
 * oturmadığı için hem dizi ([{reason, count}]) hem sözlük ({malzeme: 2})
 * hem de alternatif anahtar adları (reasons / reason_distribution /
 * fail_reasons) tolere edilir.
 */
function normalizeReasons(raw: unknown): PpcReasonItem[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item): PpcReasonItem | null => {
        if (!item || typeof item !== "object") return null;
        const rec = item as Record<string, unknown>;
        const reason = typeof rec.reason === "string" ? rec.reason : null;
        const count = Number(rec.count ?? 0);
        return reason ? { reason, count } : null;
      })
      .filter((r): r is PpcReasonItem => r !== null);
  }
  if (typeof raw === "object") {
    return Object.entries(raw as Record<string, unknown>).map(([reason, count]) => ({
      reason,
      count: Number(count ?? 0),
    }));
  }
  return [];
}

/** group_by=assignee satırlarını normalize eder (assignees / by_assignee / by_user). */
function normalizeAssignees(raw: unknown): PpcAssigneeRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): PpcAssigneeRow | null => {
      if (!item || typeof item !== "object") return null;
      const rec = item as Record<string, unknown>;
      const rawUser = rec.user;
      let user: PpcAssigneeRow["user"] | null = null;
      if (rawUser && typeof rawUser === "object") {
        const u = rawUser as Record<string, unknown>;
        if (typeof u.id === "number") {
          user = {
            id: u.id,
            name: String(u.name ?? ""),
            surname: String(u.surname ?? ""),
          };
        }
      } else if (typeof rec.user_id === "number") {
        user = {
          id: rec.user_id,
          name: String(rec.name ?? ""),
          surname: String(rec.surname ?? ""),
        };
      }
      if (!user) return null;
      return {
        user,
        committed_count: Number(rec.committed_count ?? 0),
        completed_count: Number(rec.completed_count ?? 0),
        ppc_percent: Number(rec.ppc_percent ?? 0),
      };
    })
    .filter((r): r is PpcAssigneeRow => r !== null);
}

/**
 * GET /lookahead/ppc?project_id=&week=&group_by= — week hafta içi herhangi
 * bir gün olabilir. Yanıt normalize edilir: reasons her zaman dizi,
 * assignees yalnız group_by=assignee istendiğinde dolar.
 */
export async function fetchLookaheadPpc(params: {
  project_id?: number;
  week: string;
  group_by?: "assignee";
}): Promise<LookaheadPpc> {
  const response = await api.get("/api/v1/lookahead/ppc", { params });
  const raw = response.data.data as Record<string, unknown>;
  return {
    ...(raw as unknown as Omit<LookaheadPpc, "reasons" | "assignees">),
    reasons: normalizeReasons(
      raw.reasons ?? raw.variance_reasons ?? raw.reason_distribution ?? raw.fail_reasons,
    ),
    assignees: normalizeAssignees(raw.assignees ?? raw.groups ?? raw.by_assignee ?? raw.by_user),
  };
}

// ---- Haftalık taahhüt (commit) ----

/** Görevi verilen haftaya taahhüt eder (backend Pazartesi'ye normalize eder). */
export async function commitTaskToWeek(
  taskId: number,
  week: string,
): Promise<{ id: number; committed_week: string | null }> {
  const response = await api.post(`/api/v1/tasks/${taskId}/commit`, { week });
  return response.data.data;
}

export async function uncommitTask(
  taskId: number,
): Promise<{ id: number; committed_week: string | null }> {
  const response = await api.delete(`/api/v1/tasks/${taskId}/commit`);
  return response.data.data;
}

/** POST /tasks/{id}/commit-fail — taahhüt tutmadı kaydı (neden zorunlu). */
export async function commitTaskFail(taskId: number, data: CommitFailData): Promise<void> {
  await api.post(`/api/v1/tasks/${taskId}/commit-fail`, data);
}

// ---- Görev kısıtları ----

export async function fetchTaskConstraints(taskId: number): Promise<TaskConstraint[]> {
  const response = await api.get(`/api/v1/tasks/${taskId}/constraints`);
  return response.data.data;
}

export async function createTaskConstraint(
  taskId: number,
  data: TaskConstraintFormData,
): Promise<TaskConstraint> {
  const response = await api.post(`/api/v1/tasks/${taskId}/constraints`, data);
  return response.data.data;
}

export async function updateTaskConstraint(
  constraintId: number,
  data: Partial<TaskConstraintFormData> & { status?: TaskConstraintStatus },
): Promise<TaskConstraint> {
  const response = await api.put(`/api/v1/task-constraints/${constraintId}`, data);
  return response.data.data;
}

export async function deleteTaskConstraint(constraintId: number): Promise<void> {
  await api.delete(`/api/v1/task-constraints/${constraintId}`);
}
